import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import {
  ensureTurnstilePortalStyle,
  removeTurnstileWidget,
  renderTurnstileWidget,
  resetTurnstileWidget,
  TURNSTILE_PORTAL_ATTR,
  type TurnstileWidgetId,
} from '../api/turnstile';

/**
 * Hoe lang we de paneel-open-transition volgen voordat we het rAF-loopje
 * stoppen. CSS-transition is ~250-350ms; 500ms is een ruime marge.
 */
const PANEL_TRANSITION_FOLLOW_MS = 500;

/**
 * <embrace-intake-form> — single-question intake.
 *
 * Optioneel: Cloudflare Turnstile-captcha. Als `sitekey` is gezet, wordt
 * de widget onder de textarea gerendert. Submit is dan gegate'd op een
 * geldige token. Zonder sitekey: skip Turnstile, submit zodra textarea > 2 chars.
 */
@customElement('embrace-intake-form')
export class EmbraceIntakeForm extends LitElement {
  @property({ type: String })
  sitekey = '';

  /**
   * Source-of-truth voor paneel-zichtbaarheid. We kunnen dit niet uit
   * layout afleiden: het paneel minimaliseert via `opacity: 0` + transform,
   * niet via `display: none`, dus `getBoundingClientRect()` blijft een
   * non-zero rect rapporteren ook als het paneel "dicht" is.
   */
  @property({ type: Boolean })
  panelOpen = true;

  @state() private question = '';
  @state() private captchaToken = '';
  @state() private turnstileError = '';

  @query('.turnstile-host')
  private turnstileHost?: HTMLDivElement;

  /**
   * Turnstile's interne tree-walker faalt over Shadow-DOM-grenzen heen
   * ("Cannot read properties of undefined"). Daarom mounten we 'm in
   * document.body (light DOM) en positioneren we 'm absoluut over een
   * placeholder in de form.
   */
  private turnstilePortal: HTMLDivElement | null = null;
  private widgetId: TurnstileWidgetId | null = null;
  private repositionRaf: number | null = null;
  private followRaf: number | null = null;
  private lastAppliedLeft = NaN;
  private lastAppliedTop = NaN;
  private lastAppliedWidth = NaN;

  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
    }

    form {
      padding: 16px 16px 14px;
      gap: 14px;
      display: flex;
      flex-direction: column;
      flex: 1;
    }

    .field {
      display: flex;
      flex-direction: column;
      gap: 6px;
      flex: 1;
    }

    .lbl {
      font: 600 12px/1.4 var(--ec-font-family);
      color: var(--ec-text);
      letter-spacing: .01em;
    }

    textarea {
      width: 100%;
      box-sizing: border-box;
      flex: 1;
      min-height: 120px;
      border: 1px solid var(--ec-border);
      background: #fff;
      border-radius: 10px;
      padding: 10px 12px;
      font: 400 13.5px/1.45 var(--ec-font-family);
      color: var(--ec-text);
      outline: none;
      resize: vertical;
      transition: border-color .12s, box-shadow .12s;
    }
    textarea:focus {
      border-color: var(--ec-primary);
      box-shadow: 0 0 0 3px rgba(12,28,71,.12);
    }

    .turnstile-wrap {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }
    .turnstile-host {
      min-height: 65px;
    }
    .turnstile-error {
      font: 500 11.5px/1.4 var(--ec-font-family);
      color: var(--ec-error);
    }

    .cta {
      margin-top: 2px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 12px 20px;
      background: var(--ec-primary);
      color: #fff;
      border: 0;
      border-radius: 12px;
      font: 700 14px/1 var(--ec-font-family);
      cursor: pointer;
      transition: background .12s, transform .08s;
      box-shadow: 0 10px 24px -14px rgba(12,28,71,.5);
    }
    .cta:hover:not(:disabled) { background: #172a5c; }
    .cta:active:not(:disabled) { transform: scale(.99); }
    .cta:disabled {
      background: #C8CCD4;
      cursor: not-allowed;
      box-shadow: none;
    }

    .legal {
      margin: 2px 0 0;
      font: 400 10.5px/1.45 var(--ec-font-family);
      color: #8C8D8F;
      text-align: center;
    }
    .legal a { color: var(--ec-primary); text-decoration: underline; }
  `;

  override firstUpdated(): void {
    if (this.sitekey) void this.mountTurnstile();
    if (this.panelOpen) this.followPanelTransition();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unmountTurnstile();
  }

  private async mountTurnstile(): Promise<void> {
    if (!this.turnstileHost || this.turnstilePortal) return;

    ensureTurnstilePortalStyle();

    const portal = document.createElement('div');
    portal.setAttribute(TURNSTILE_PORTAL_ATTR, '');
    portal.style.cssText =
      'position: fixed; z-index: 1000001; pointer-events: auto;';
    document.body.appendChild(portal);
    this.turnstilePortal = portal;

    // Synchroon: Cloudflare's renderer leest container-width direct in
    // `render()` en valt terug op z'n minimum (300px) als de portal nog
    // geen breedte heeft. rAF is hier dus te laat.
    this.applyPortalPosition();
    window.addEventListener('scroll', this.updatePortalPosition, true);
    window.addEventListener('resize', this.updatePortalPosition);

    try {
      const widgetId = await renderTurnstileWidget(portal, {
        sitekey: this.sitekey,
        theme: 'light',
        language: 'nl',
        size: 'flexible',
        callback: (token) => {
          this.captchaToken = token;
          this.turnstileError = '';
        },
        'error-callback': (code) => {
          this.turnstileError = `Captcha fout (${code}). Vernieuw de pagina om opnieuw te proberen.`;
          this.captchaToken = '';
        },
        'expired-callback': () => {
          this.captchaToken = '';
          if (this.widgetId) resetTurnstileWidget(this.widgetId);
        },
        'timeout-callback': () => {
          this.captchaToken = '';
          if (this.widgetId) resetTurnstileWidget(this.widgetId);
        },
      });
      // Disconnect kan tijdens de await zijn gebeurd. In dat geval is de
      // portal al opgeruimd door unmountTurnstile() — niet stiekem een
      // widgetId vasthouden naar een verdwenen DOM-node.
      if (!this.turnstilePortal) {
        removeTurnstileWidget(widgetId);
        return;
      }
      this.widgetId = widgetId;
      this.applyPortalPosition();
    } catch (err) {
      this.turnstileError =
        'De beveiligingscheck kon niet geladen worden. Controleer je verbinding en probeer het opnieuw.';
      console.error('[IntakeForm] Turnstile mount failed:', err);
    }
  }

  private unmountTurnstile(): void {
    if (this.widgetId) {
      removeTurnstileWidget(this.widgetId);
      this.widgetId = null;
    }
    if (this.turnstilePortal) {
      this.turnstilePortal.remove();
      this.turnstilePortal = null;
    }
    window.removeEventListener('scroll', this.updatePortalPosition, true);
    window.removeEventListener('resize', this.updatePortalPosition);
    if (this.repositionRaf !== null) {
      cancelAnimationFrame(this.repositionRaf);
      this.repositionRaf = null;
    }
    if (this.followRaf !== null) {
      cancelAnimationFrame(this.followRaf);
      this.followRaf = null;
    }
  }

  /**
   * Synchrone variant. Cloudflare's renderer leest container-width direct
   * bij mount, dus rAF-coalescing is daar te laat. Cachet de laatst-
   * geschreven left/top/width zodat de follow-loop tijdens een transition
   * geen identieke styles herschrijft (CSSOM-parse vermijden).
   */
  private applyPortalPosition(): void {
    const portal = this.turnstilePortal;
    if (!portal) return;
    if (!this.panelOpen) {
      portal.style.display = 'none';
      // Cache invalideren: bij re-show willen we de eerste meting
      // altijd schrijven, ook als de rect numeriek hetzelfde is.
      this.lastAppliedLeft = NaN;
      return;
    }
    const host = this.turnstileHost;
    if (!host) return;
    const rect = host.getBoundingClientRect();
    portal.style.display = 'block';
    if (rect.left === this.lastAppliedLeft
      && rect.top === this.lastAppliedTop
      && rect.width === this.lastAppliedWidth) return;
    this.lastAppliedLeft = rect.left;
    this.lastAppliedTop = rect.top;
    this.lastAppliedWidth = rect.width;
    portal.style.left = `${rect.left}px`;
    portal.style.top = `${rect.top}px`;
    portal.style.width = `${rect.width}px`;
  }

  private readonly updatePortalPosition = (): void => {
    if (this.repositionRaf !== null) return;
    this.repositionRaf = requestAnimationFrame(() => {
      this.repositionRaf = null;
      this.applyPortalPosition();
    });
  };

  /**
   * Volgt de paneel-open-transition frame-voor-frame. Het paneel animeert
   * via `transform: scale(.98) → 1` + `translateY(12px) → 0`. Een eenmalige
   * meting bij `panelOpen=true` vangt alleen de start-positie; de eind-
   * positie ligt tens-of-pixels verderop. We kunnen geen `transitionend`
   * gebruiken — die fired niet voor de transform-tussenframes, en
   * vereist Shadow-DOM-boundary jugging.
   */
  private followPanelTransition(): void {
    if (this.followRaf !== null) cancelAnimationFrame(this.followRaf);
    const start = performance.now();
    const tick = (now: number) => {
      this.followRaf = null;
      if (!this.turnstilePortal || !this.panelOpen) return;
      this.applyPortalPosition();
      if (now - start < PANEL_TRANSITION_FOLLOW_MS) {
        this.followRaf = requestAnimationFrame(tick);
      }
    };
    this.followRaf = requestAnimationFrame(tick);
  }

  override updated(changed: Map<string, unknown>): void {
    if (!this.turnstilePortal) return;
    // Layout binnen het paneel kan veranderen (textarea-resize,
    // error-message-toon). Hersync de portal-positie via de
    // rAF-gecoalesceerde variant — geen kosten als rect onveranderd blijft.
    this.updatePortalPosition();
    if (changed.has('panelOpen') && this.panelOpen) {
      this.followPanelTransition();
    }
  }

  override render() {
    const questionOk = this.question.trim().length > 2;
    const captchaOk = !this.sitekey || this.captchaToken.length > 0;
    const canSubmit = questionOk && captchaOk;

    return html`
      <form part="intake-form" @submit=${this.onSubmit}>
        <label class="field" part="intake-field">
          <span class="lbl">Je vraag</span>
          <textarea
            rows="5"
            autofocus
            placeholder="Omschrijf je vraag kort — we gebruiken dit om je aan de juiste medewerker te koppelen…"
            .value=${this.question}
            @input=${(e: Event) => (this.question = (e.target as HTMLTextAreaElement).value)}
          ></textarea>
        </label>

        ${this.sitekey
          ? html`
              <div class="turnstile-wrap">
                <div class="turnstile-host" aria-label="Beveiligingscheck"></div>
                ${this.turnstileError
                  ? html`<div class="turnstile-error" role="alert">${this.turnstileError}</div>`
                  : nothing}
              </div>
            `
          : nothing}

        <button
          type="submit"
          class="cta"
          part="intake-submit"
          ?disabled=${!canSubmit}
        >
          Start gesprek
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M5 12h14m-6-6 6 6-6 6" />
          </svg>
        </button>

        <p class="legal">
          Door op <strong>Start gesprek</strong> te klikken ga je akkoord met
          onze <a href="#" @click=${(e: Event) => e.preventDefault()}>privacyverklaring</a>.
          Je chat wordt opgeslagen voor kwaliteitsdoeleinden.
        </p>
      </form>
    `;
  }

  private onSubmit(e: Event) {
    e.preventDefault();
    const question = this.question.trim();
    if (question.length < 3) return;
    if (this.sitekey && !this.captchaToken) return;

    this.dispatchEvent(
      new CustomEvent('intake-submit', {
        detail: { question, captchaToken: this.captchaToken },
        bubbles: true,
        composed: true,
      }),
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'embrace-intake-form': EmbraceIntakeForm;
  }
}
